/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

import { createGustGrains, createShaderProgram, type GustGrains } from "@bfly/engine";
import { FRAGMENT_SHADER, LIFT_SHADER, VERTEX_SHADER } from "./field-shaders.js";
import { WAKE_SAMPLES, WISP_SLOTS } from "./wisps.js";

// The lift program's uniforms.
function liftUniforms(gl: WebGL2RenderingContext, liftProgram: WebGLProgram) {
  const liftUniform = (name: string): WebGLUniformLocation | null =>
    gl.getUniformLocation(liftProgram, name);
  return {
    cell: liftUniform("u_cell"),
    origin: liftUniform("u_origin"),
    ringSlot: liftUniform("u_ringSlot"),
    ringTime: liftUniform("u_ringTime"),
    ringStrength: liftUniform("u_ringStrength"),
    ringCount: liftUniform("u_ringCount"),
    wisp: liftUniform("u_wisp"),
    wakeBox: liftUniform("u_wakeBox"),
    wakeData: liftUniform("u_wakeData"),
    wakeThick: liftUniform("u_wakeThick"),
    grains: liftUniform("u_grains"),
    grainOffset: liftUniform("u_grainOffset"),
  };
}

// The field program's uniforms.
function fieldUniforms(gl: WebGL2RenderingContext, program: WebGLProgram) {
  const uniform = (name: string): WebGLUniformLocation | null =>
    gl.getUniformLocation(program, name);
  return {
    charsetLen: uniform("u_charsetLen"),
    tiers: uniform("u_tiers"),
    time: uniform("u_time"),
    patternShift: uniform("u_patternShift"),
    height: uniform("u_height"),
    cell: uniform("u_cell"),
    origin: uniform("u_origin"),
    grid: uniform("u_grid"),
    box: uniform("u_box"),
    cap: uniform("u_cap"),
    breakouts: uniform("u_breakouts"),
    breakoutCount: uniform("u_breakoutCount"),
    band: uniform("u_band"),
    colors: uniform("u_colors"),
    colorCount: uniform("u_colorCount"),
    ringColors: uniform("u_ringColors"),
    ringColorCount: uniform("u_ringColorCount"),
    ringCount: uniform("u_ringCount"),
    blend: uniform("u_blend"),
    ground: uniform("u_ground"),
    pad: uniform("u_pad"),
    clock: uniform("u_clock"),
    twinkleRate: uniform("u_twinkleRate"),
    wisp: uniform("u_wisp"),
    wispSource: uniform("u_wispSource"),
    wispTime: uniform("u_wispTime"),
    wakeBox: uniform("u_wakeBox"),
    trail: uniform("u_trail"),
    trailBox: uniform("u_trailBox"),
    pop: uniform("u_pop"),
    popSpin: uniform("u_popSpin"),
    lift: uniform("u_lift"),
  };
}

/** The field's programs, their uniforms, and the lift pass's framebuffer. */
export interface FieldGpu {
  liftProgram: WebGLProgram;
  l: ReturnType<typeof liftUniforms>;
  program: WebGLProgram;
  u: ReturnType<typeof fieldUniforms>;
  liftFramebuffer: WebGLFramebuffer | null;
  /** A copy of the lift pass's ages, which the field reads back (field.ts). */
  agesFramebuffer: WebGLFramebuffer | null;
  /** The gust's grains (gust-grains.ts in the engine), with their texture on unit 3. */
  grains: GustGrains;
}

// Sets up the two programs, their uniforms, and the textures. There are two passes:
// `LIFT_SHADER` works out each cell's lift into `liftTexture`, then `FRAGMENT_SHADER` draws the
// field. The field program stays current except inside `draw()` in field.ts. Throws when a shader
// doesn't compile or link.
//
// The programs link in parallel, off the main thread where the browser can
// (`createShaderProgram`). A blocking link froze the page on the first few visits, before the
// GPU's shader cache had them. If one program fails, the others get deleted before the error is
// rethrown.
export async function setupGpu(gl: WebGL2RenderingContext): Promise<FieldGpu> {
  // The field's two programs and the grains' three link at once.
  const [linked, grainsSetup] = await Promise.all([
    Promise.allSettled([
      createShaderProgram(gl, VERTEX_SHADER, LIFT_SHADER),
      createShaderProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER),
    ]),
    Promise.allSettled([createGustGrains(gl, 3)]),
  ]);
  const [lift, main] = linked;
  const [grainsDone] = grainsSetup;
  if (
    lift.status === "rejected" ||
    main.status === "rejected" ||
    grainsDone.status === "rejected"
  ) {
    for (const result of linked) if (result.status === "fulfilled") gl.deleteProgram(result.value);
    throw [...linked, ...grainsSetup].find(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    )?.reason;
  }
  const grains = grainsDone.value;
  const liftProgram = lift.value;
  const program = main.value;
  const l = liftUniforms(gl, liftProgram);
  gl.useProgram(program);
  const u = fieldUniforms(gl, program);

  // Each texture keeps its own unit:
  // - 0: the atlas
  // - 1: the wake rings
  // - 2: the lift
  // - 3: the grains
  // - 4: the ages
  // - 5: the palette weights
  // - 6: the ages' copy
  // `layout()` in field.ts sizes 2, 4, 5, and 6 to the grid.
  function texture(unit: number, filter: GLenum): WebGLTexture {
    const created = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, created);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return created;
  }
  // Linear, so a glyph that a wave grows stays smooth (see `glyph()` in the shader).
  texture(0, gl.LINEAR);
  // One RGBA32F texel per wake ring. Float textures can only filter as nearest.
  texture(1, gl.NEAREST);
  gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA32F, WAKE_SAMPLES, WISP_SLOTS);
  const liftTexture = texture(2, gl.NEAREST);
  const liftFramebuffer = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, liftFramebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, liftTexture, 0);
  // Each wave's age at each cell (wave-ages.ts), the lift pass's second output.
  const agesTexture = texture(4, gl.NEAREST);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, agesTexture, 0);
  // Each wave's palette weight at each cell, the third output, which the field reads.
  const blendTexture = texture(5, gl.NEAREST);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT2, gl.TEXTURE_2D, blendTexture, 0);
  gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2]);
  // The ages' copy, so the next lift pass can write the ages while a read waits for its fence.
  const agesCopy = texture(6, gl.NEAREST);
  const agesFramebuffer = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, agesFramebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, agesCopy, 0);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.uniform1i(u.lift, 2);
  gl.uniform1i(u.blend, 5);
  gl.useProgram(liftProgram);
  gl.uniform1i(l.wakeData, 1);
  gl.uniform1i(l.grains, 3);
  gl.useProgram(program);
  return { liftProgram, l, program, u, liftFramebuffer, agesFramebuffer, grains };
}
